import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  ContractTemplate,
  ContractTemplateField,
  CreateContractTemplatePayload,
  CreateContractTemplateVersionPayload,
  SetContractTemplateFieldsPayload,
} from '../types';

/**
 * Calls osprey-api's `/contract-templates` routes (PE-2144 backend / PE-2145 FE).
 * Every route is TCS-staff only (CollectiveOnlyGuard) + MERCHANT_ONBOARDING perm,
 * enforced server-side; this client only sends the bearer that ApiFetch already
 * attaches. Not tenant-scoped — contract templates are platform-level.
 *
 * `ApiFetch.put` issues a PATCH on the wire (see shared/utils/fetch.ts), so
 * `activate` maps to `PATCH /contract-templates/:id/activate` and `setFields`
 * to `PUT /contract-templates/:id/fields` via a dedicated putRaw — see below.
 */
export const contractTemplatesApi = {
  list: async (): Promise<ContractTemplate[]> => {
    const response = await ApiFetch.get<Enveloped<ContractTemplate[]>>('/contract-templates');
    return unwrap(response);
  },

  get: async (id: string): Promise<ContractTemplate> => {
    const response = await ApiFetch.get<Enveloped<ContractTemplate>>(`/contract-templates/${id}`);
    return unwrap(response);
  },

  create: async (data: CreateContractTemplatePayload): Promise<ContractTemplate> => {
    const response = await ApiFetch.post<Enveloped<ContractTemplate>>('/contract-templates', data);
    return unwrap(response);
  },

  createVersion: async (
    name: string,
    data: CreateContractTemplateVersionPayload,
  ): Promise<ContractTemplate> => {
    const response = await ApiFetch.post<Enveloped<ContractTemplate>>(
      `/contract-templates/${encodeURIComponent(name)}/versions`,
      data,
    );
    return unwrap(response);
  },

  activate: async (id: string): Promise<ContractTemplate> => {
    // ApiFetch.put issues a PATCH on the wire → PATCH /contract-templates/:id/activate.
    const response = await ApiFetch.put<Enveloped<ContractTemplate>>(
      `/contract-templates/${id}/activate`,
      {},
    );
    return unwrap(response);
  },

  getFields: async (id: string): Promise<ContractTemplateField[]> => {
    const response = await ApiFetch.get<Enveloped<ContractTemplateField[]>>(
      `/contract-templates/${id}/fields`,
    );
    return unwrap(response);
  },

  setFields: async (
    id: string,
    data: SetContractTemplateFieldsPayload,
  ): Promise<ContractTemplateField[]> => {
    // Backend route is PUT /contract-templates/:id/fields. ApiFetch.put emits PATCH,
    // so use legacy_put which keeps the verb as a real PUT (see shared/utils/fetch.ts).
    const response = await ApiFetch.legacy_put<Enveloped<ContractTemplateField[]>>(
      `/contract-templates/${id}/fields`,
      data,
    );
    return unwrap(response);
  },
};
